import { Box, Button, Divider, Flex, Group, LoadingOverlay, Modal, Paper, ScrollArea, Select, SimpleGrid, Space, Text, Textarea, TextInput, Title } from '@mantine/core';
import React, { memo, useContext, useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import classes from './Packaging.module.css';
import { baseUrl } from '../../../../../Constants/constant';
import useFetch from '../../../../../Customhooks/useFetch';
import BoxPackaging from './Box/Box.component';
import PalletsPackaging from './Palletes/Pallets.component';
import { authContext } from '../../../../../context/AuthProvider.component';
import { useDisclosure } from '@mantine/hooks';


interface BoxFormField {
    id_comp: number;
    id: number | null;
    no_of_product: string;
    co2_embodiment: string;
    weight: string;
    box_id: string | null;
}

interface PalletFormField {
    id_comp: number;
    id: number | null;
    no_of_box: string;
    co2_embodiment: string;
    weight: string;
    pallet_id: string | null;
}

interface ProductForm {
    compute_id: number,
    is_draft: number,
    step: string,
    file_name: string | null,
    package_components?: Array<{
        id: number | null;
        no_of_product_per_box: string;
        co2_embodiment: string;
        weight: string;
        box_type_id: string | null;
    }>;
    pallet_components?: Array<{
        id: number | null;
        no_of_box: string;
        co2_embodiment: string;
        weight: string;
        pallet_id: string | null;
    }>;
}
type ErrorsType = {
    file_name?: string;
    form?: string;
};


interface PackagingProps {
    onComplete: () => void;
    onBack: () => void;
}

const Packaging: React.FC<PackagingProps> = ({
    onComplete,
    onBack,
}) => {
    const navigate = useNavigate();
    // const { id } = useParams();
    const token = localStorage.getItem("token") || "";
    const { computeId, draftLca, setDraftLca } = useContext<any>(authContext)
    const [boxFormData, setBoxFormData] = useState<BoxFormField[]>([]);
    const [palletFormData, setPalletFormData] = useState<PalletFormField[]>([]);
    const [isValid, setIsValid] = useState(false);
    const [requestData, setRequestData] = useState<ProductForm | null>(null);
    const [showErrors, setShowErrors] = useState(false);
    const [saveMode, setSaveMode] = useState<'next' | 'draft' | 'skip'>('next');
    const [initialBoxCharacteristics, setInitialBoxCharacteristics] = useState<BoxFormField[]>([]);
    const [initialPalletCharacteristics, setInitialPalletCharacteristics] = useState<PalletFormField[]>([]);
    const [editId, setEditId] = useState<number>()
    const [opened, { open, close }] = useDisclosure(false);
    const [errors, setErrors] = useState<ErrorsType>({});
    const [fileName, setFileName] = useState<string>('');
    const [isLoading, setIsLoading] = useState(false);

    const fetchConfig = {
        method: "GET",
        headers: {
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
            token: token,
        },
    };
    const { data: getDaataPackageid, loading: packageLoading, error, fetchData: fetchPackageDetails } = useFetch(fetchConfig);
    // Stable useFetch configuration - don't include requestData in dependencies
    const packagingConfig = useMemo(() => ({
        method: 'POST', // We'll handle method dynamically in the API call
        headers: {
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
            token: token,
        },
    }), [token]);

    const {
        data: packagingData,
        error: packagingError,
        loading: packagingLoading,
        fetchData: postPackaging
    } = useFetch(packagingConfig);

    useEffect(() => {
        if (!computeId) return;
        fetchPackageDetails(`${baseUrl}compute/lca/${computeId}?is_draft=${draftLca}`);
    }, [computeId]);

    useEffect(() => {
        if (!getDaataPackageid?.data?.rows[0]?.ComputePackages) return;
        if (!getDaataPackageid?.data?.rows[0]?.ComputePallets) return;
        if (getDaataPackageid?.data) {
            if (getDaataPackageid?.data?.rows[0]?.ComputePallets) {
                setEditId(getDaataPackageid?.data?.rows[0]?.ComputePallets[0]?.id)
                // Transform Box Fields

                const transformedBoxFields = getDaataPackageid?.data?.rows[0]?.ComputePackages?.map((box: any, index: number) => ({
                    id_comp: index + 1,
                    id: box.id ?? null,
                    no_of_product: box.no_of_product_per_box || '',
                    co2_embodiment: box.co2_embodied || '',
                    weight: box.weight || '',
                    box_id: box.box_type_id || null,
                }));

                const transformedPalletFields = getDaataPackageid?.data?.rows[0]?.ComputePallets?.map((pallets: any, index: number) => ({
                    id_comp: index + 1,
                    id: pallets.id ?? null,
                    no_of_box: pallets.no_of_box || '',
                    co2_embodiment: pallets.co2_embodied || '',
                    weight: pallets.weight || '',
                    pallet_id: pallets.pallet_id || null,
                }));

                setInitialBoxCharacteristics(transformedBoxFields);
                setInitialPalletCharacteristics(transformedPalletFields);
                setBoxFormData(transformedBoxFields);
                setPalletFormData(transformedPalletFields);
            }
        }
    }, [getDaataPackageid]);

    const showNotification = (type: 'success' | 'error', message: string) => {
        notifications.show({
            title: type === 'success' ? 'Success' : 'Error',
            message,
            color: type === 'success' ? 'green' : 'red',
            // classNames: classes,
        });
    };

    const handleBoxFieldsChange = (boxFields: BoxFormField[]) => {
        setBoxFormData(boxFields);
    };
    const handlePalletFieldsChange = (palletFields: PalletFormField[]) => {
        setPalletFormData(palletFields);
    };

    const handleValidationChange = (valid: boolean) => {
        setIsValid(valid);
    };

    const handleSave = async (e: any, mode: 'next' | 'draft' | 'skip') => {
        e.preventDefault();
        if (mode === 'skip') {
            onComplete();
            return;
        }

        // Set save mode first
        setSaveMode(mode);
        mode === 'next' ? setDraftLca(0) : setDraftLca(1);
        // Comprehensive validation
        const isFormValid = validateForm(mode);

        setShowErrors(true);
        if (!isFormValid) {
            showNotification('error', 'Please fill all required fields');
            return;
        }

        // Proceed with saving if validation passes
        const data: ProductForm = {
            compute_id: computeId,
            is_draft: mode === 'draft' ? 1 : 0,
            step: "Packaging",
            file_name: mode === 'draft' ? fileName : null,
            package_components: boxFormData.map(field => {
                const boxData: any = {
                    no_of_product_per_box: field.no_of_product,
                    co2_embodied: field.co2_embodiment,
                    // weight: field.weight,
                    box_type_id: field.box_id,
                };
                if (field.id) {
                    boxData.id = field.id;
                }
                return boxData;
            }),
            pallet_components: palletFormData.map(field => {
                const palletData: any = {
                    no_of_box: field.no_of_box,
                    co2_embodied: field.co2_embodiment,
                    // weight: field.weight,
                    pallet_id: field.pallet_id,
                };
                if (field.id) {
                    palletData.id = field.id;
                }
                return palletData;
            }),
        };

        setRequestData(data);
    };

    const validateForm = (mode: 'next' | 'draft' | 'skip'): boolean => {
        let newErrors: ErrorsType = {};

        // Validate file name if in draft mode
        if (mode === 'draft') {
            if (!fileName.trim()) {
                newErrors.file_name = "File name is required";
            } else if (fileName.length < 3) {
                newErrors.file_name = "File name must be at least 3 characters long";
            } else if (fileName.length > 50) {
                newErrors.file_name = "File name must be less than 50 characters";
            }
        }

        // Validate form fields
        if (!isValid) {
            newErrors.form = "Please fill all required fields";
        }

        // Set errors and return validation status
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Handle successful API response
    const handleSuccessResponse = (result: any) => {
        if (saveMode === 'draft') {
            // Close modal first
            close();
            
            // Show success notification
            showNotification('success', 'File Saved in Draft Successfully');
            
            // Navigate if fileName exists
            if (fileName && fileName.trim()) {
                navigate('/draft');
            }
        } else {
            onComplete();
            showNotification('success', result?.message || 'Packaging Data Saved Successfully');
        }
        
        // Reset requestData to prevent duplicate processing
        setRequestData(null);
    };


    useEffect(() => {
        const makeApiCall = async () => {
            if (requestData) {
                const url = editId ? `${baseUrl}compute/lca/updatePackage-Pallet` : `${baseUrl}compute/lca/addPackage-Pallet`;
                
                try {
                    setIsLoading(true);
                    
                    const response = await fetch(url, {
                        method: editId ? 'PUT' : 'POST',
                        headers: {
                            "Content-Type": "application/json",
                            "Access-Control-Allow-Origin": "*",
                            token: token,
                        },
                        body: JSON.stringify(requestData)
                    });
                    
                    if (!response.ok) {
                        throw new Error(`HTTP error! Status: ${response.status}`);
                    }
                    
                    const result = await response.json();
                    
                    // Directly handle the success response
                    handleSuccessResponse(result);
                    
                } catch (error) {
                    console.error('API Error:', error);
                    showNotification('error', 'An error occurred while saving');
                } finally {
                    setIsLoading(false);
                }
            }
        };

        makeApiCall();
    }, [requestData, editId, token, showNotification]);

    // Optimized response handling
    useEffect(() => {
        if (packagingData?.errors || packagingError) {
            showNotification('error', packagingData?.message || packagingError?.message);
            return;
        }

        if (packagingData && Object.keys(packagingData).length > 0) {
            if (saveMode === 'draft') {
                // Close modal first
                close();
                
                // Show success notification
                showNotification('success', 'File Saved in Draft Successfully');
                
                // Navigate if fileName exists
                if (fileName && fileName.trim()) {
                    navigate('/draft');
                }
            } else {
                onComplete();
                showNotification('success', packagingData?.message || 'Packaging Data Saved Successfully');
            }
            
            // Reset requestData to prevent duplicate processing
            setRequestData(null);
        }
    }, [packagingData, packagingError, saveMode, fileName, navigate, close, showNotification]);
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFileName(e.target.value);

        // Safely check and clear the file_name error
        if (errors?.file_name) {
            setErrors(prev => ({ ...prev, file_name: undefined }));
        }
    };

    return (
        <>
            <LoadingOverlay
                visible={packageLoading}
                zIndex={1000}
                overlayProps={{
                    radius: 'sm',
                    blur: 2,
                    style: {
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%'
                    }
                }}
                loaderProps={{ color: '#404040', type: 'bars' }}
            />
            <Paper shadow="xs" p="xs" radius="md">
                <Box ml={5}>
                    <ScrollArea h={300} scrollbarSize={2}>
                        <Title order={4}>Packaging</Title>
                        <Space h="lg" />
                        {/* <Space h="xs" /> */}
                        <BoxPackaging
                            onFieldsChange={handleBoxFieldsChange}
                            onValidationChange={handleValidationChange}
                            showErrors={showErrors}
                            initialFields={initialBoxCharacteristics}
                        />

                        <PalletsPackaging
                            onFieldsChange={handlePalletFieldsChange}
                            onValidationChange={handleValidationChange}
                            showErrors={showErrors}
                            initialFields={initialPalletCharacteristics}
                        />
                    </ScrollArea>
                    <Space h="lg" />
                    <Space h="sm" />
                    <Modal centered opened={opened} onClose={close} title="Save in Draft">
                        <TextInput
                            autoFocus
                            label="File Name"
                            value={fileName}
                            onChange={handleChange}
                            variant="filled"
                            withAsterisk
                            mb="lg"
                            placeholder="Please enter file name"
                            radius={6}
                            error={errors?.file_name}
                        />
                        <Divider h={'md'} />
                        <Flex justify="flex-end">
                            <Button
                                variant="gradient"
                                gradient={{ from: '#404040', to: '#404040', deg: 90 }}
                                radius={6}
                                onClick={(e) => handleSave(e, 'draft')}
                                size="md"
                            >
                                Save
                            </Button>
                        </Flex>

                    </Modal>
                    <Flex
                        justify="flex-end"
                    >
                        <Group >
                            <Button
                                variant="default"
                                radius={6}
                                onClick={open}
                                size="md"
                            >
                                Save to Draft
                            </Button>
                            <Button
                                variant="default"
                                radius={6}
                                size="md"
                                onClick={onBack}
                            >
                                Back
                            </Button>
                            <Button
                                variant="default"
                                radius={6}
                                onClick={(e) => handleSave(e, 'skip')}
                                size="md"
                            >
                                Skip
                            </Button>
                            <Button
                                variant="gradient"
                                gradient={{ from: '#404040', to: '#404040', deg: 90 }}
                                radius={6}
                                onClick={(e) => handleSave(e, 'next')}
                                loading={isLoading}
                                disabled={isLoading}
                                size="md"
                            >
                                Next
                            </Button>
                        </Group>
                    </Flex>
                </Box>
            </Paper>
        </>
    );
};

export default memo(Packaging);